import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  FilterList
} from '@mui/icons-material';
// import CategoryFilters from '../components/CategoryFilters';
import ProductCard from '../components/ProductCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getCategoryInfo, getCategoryProducts } from '../api/apiClient';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterInfo, setFilterInfo] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Новые состояния для пагинации по прокрутке
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const limit = 36; // Количество товаров на странице
  
  // Состояние для подкатегорий
  const [subcategories, setSubcategories] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  
  // Устанавливаем заголовок страницы после определения categoryInfo
  useDocumentTitle(categoryInfo?.name || 'Категория');
  
  // Обновляем заголовок при изменении информации о категории
  useEffect(() => {
    if (categoryInfo?.name) {
      document.title = `${categoryInfo.name} | ZARA в Россию через @bayer_antalya`;
    }
  }, [categoryInfo?.name]);

  useEffect(() => {
    if (page === 1) {
      // Первая страница - загружаем заново
      fetchCategoryInfo();
      fetchCategoryProducts();
    } else if (page > 1) {
      // Дополнительные страницы - добавляем к существующим результатам
      fetchMoreProducts();
    }
  }, [categoryId, page, sortBy]);

  // Функция для получения информации о категории и её подкатегориях
  const fetchCategoryInfo = async () => {
    try {
      const response = await getCategoryInfo(categoryId);
      
      if (response.data) {
        setCategoryInfo(response.data);
        
        // Извлекаем подкатегории из структуры данных
        const extractedSubcategories = response.data.subcategories || [];
        setSubcategories(extractedSubcategories);
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки информации о категории:', err);
      setSubcategories([]);
    }
  };

  const fetchCategoryProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const discountDebug = [];
      
      // Используем наш backend API
      const response = await getCategoryProducts(categoryId);
      
      // Обрабатываем данные категории
      if (response.data && response.data.productGroups) {
        // Отслеживаем просмотр категории в Яндекс.Метрике
        if (window.trackCategoryView && categoryInfo) {
          const totalProducts = response.data.productGroups.reduce((total, group) => 
            total + (group.elements?.reduce((groupTotal, element) => 
              groupTotal + (element.commercialComponents?.length || 0), 0) || 0), 0);
          
          window.trackCategoryView(
            categoryId,
            categoryInfo.name,
            totalProducts
          );
        }
        
        const allProducts = [];
        
        response.data.productGroups.forEach(group => {
          if (group.elements) {
            group.elements.forEach(element => {
              if (element.commercialComponents) {
                element.commercialComponents.forEach(component => {
                  if (component.type === 'Product') {
                                         // Ищем основное изображение товара
                     let mainImage = null;
                     if (component.detail?.colors && component.detail.colors.length > 0) {
                       const firstColor = component.detail.colors[0];
                       // Сначала пробуем pdpMedia (основное изображение)
                       if (firstColor.pdpMedia?.extraInfo?.deliveryUrl) {
                         mainImage = firstColor.pdpMedia.extraInfo.deliveryUrl;
                       }
                       // Если нет pdpMedia, берем первое из xmedia
                       else if (firstColor.xmedia && firstColor.xmedia.length > 0) {
                         mainImage = firstColor.xmedia[0].extraInfo?.deliveryUrl;
                       }
                     }
                     
                     // Извлекаем информацию о цветах
                     const colors = component.detail?.colors || [];
                     
                     const productItem = {
                       id: component.id,
                       name: component.name,
                       reference: component.reference,
                       displayReference: component.detail?.displayReference,
                       price: component.price,
                       futurePrice: component.futurePrice,
                       sectionName: component.sectionName,
                       familyName: component.familyName,
                       availability: component.availability,
                       colors: colors,
                       mainImage: mainImage
                     };

                     if (component.futurePrice) {
                       discountDebug.push({
                         id: component.id,
                         displayReference: component.detail?.displayReference || component.reference,
                         currentPrice: component.price,
                         futurePrice: component.futurePrice?.price,
                         startsAt: component.futurePrice?.description
                       });
                     }

                     allProducts.push(productItem);
                     

                  }
                });
              }
            });
          }
        });
        
        setProducts(allProducts);
        setFilteredProducts([]); // Сбрасываем отфильтрованные товары
        
        // Устанавливаем отображаемые товары для первой страницы
        const firstPageProducts = allProducts.slice(0, limit);
        setDisplayedProducts(firstPageProducts);
        
        // Обновляем информацию о пагинации
        setTotalPages(Math.ceil(allProducts.length / limit));
        setHasNextPage(allProducts.length > limit);

        console.log('[CategoryPage] Загрузка категории', {
          categoryId,
          totalProducts: allProducts.length,
          discountCount: discountDebug.length
        });
        if (discountDebug.length > 0) {
          console.log('[CategoryPage] Найдены товары со скидкой', {
            categoryId,
            count: discountDebug.length,
            samples: discountDebug.slice(0, 5)
          });
        } else {
          console.log('[CategoryPage] Товаров со скидкой не найдено в ответе', { categoryId });
        }
      } else {
        setProducts([]);
        setError('Нет товаров в категории');
      }
      
    } catch (err) {
      console.error('❌ Ошибка загрузки категории:', err);
      setError('Ошибка загрузки товаров категории');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };



  // Функция для загрузки дополнительных товаров (ленивая подгрузка)
  const fetchMoreProducts = async () => {
    if (loadingMore || !hasNextPage) return;
    
    try {
      setLoadingMore(true);
      
      // Вычисляем диапазон товаров для текущей страницы
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const newProducts = products.slice(startIndex, endIndex);
      
      // Добавляем новые товары к уже отображаемым
      setDisplayedProducts(prev => [...prev, ...newProducts]);
      
      // Обновляем информацию о пагинации
      setHasNextPage(endIndex < products.length);
      
    } catch (err) {
      console.error('❌ Ошибка загрузки дополнительных товаров:', err);
      setError('Ошибка при загрузке дополнительных товаров');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  // Функция для ленивой подгрузки при прокрутке
  const handleScroll = () => {
    if (loadingMore || !hasNextPage) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Загружаем следующую страницу когда пользователь приближается к концу
    if (scrollTop + windowHeight >= documentHeight - 100) {
      setPage(prev => prev + 1);
    }
  };

  // Добавляем слушатель прокрутки
  useEffect(() => {
    if (hasNextPage && !loadingMore) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [hasNextPage, loadingMore]);

  // Функция для перехода к подкатегории
  const handleSubcategoryClick = (subcategory) => {
    if (subcategory.id) {
      // Переходим на страницу подкатегории
      navigate(`/category/${subcategory.id}`);
    }
  };

  const handleFiltersChange = (filters, filterInfo) => {
    setActiveFilters(filters);
    setFilterInfo(filterInfo);
    setPage(1); // Сбрасываем страницу при изменении фильтров
    
    // Применяем фильтры к товарам
    applyFiltersToProducts(filters, filterInfo);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setFilterInfo({});
    setFilteredProducts([]);
    setPage(1);
    setTotalPages(Math.ceil(products.length / limit)); // Возвращаем исходную пагинацию
    setHasNextPage(products.length > limit);
    setDisplayedProducts(products.slice(0, limit));
  };

  const applyFiltersToProducts = (filters, filterInfo) => {
    if (!products.length) return;
    
    let filtered = [...products];
    
    // Фильтрация по цвету, размеру и другим параметрам
    Object.keys(filters).forEach(filterKey => {
      if (filterKey === 'priceRange') return; // Цену обрабатываем отдельно
      
      if (filters[filterKey] && filters[filterKey].length > 0) {
        const filterData = filterInfo[filterKey];
        if (filterData && filterData.availableProducts) {
          // Фильтруем по ID товаров из фильтра
          filtered = filtered.filter(product => 
            filterData.availableProducts.includes(product.id)
          );
        }
      }
    });
    
    // Фильтрация по цене
    if (filters.priceRange) {
      const [minPrice, maxPrice] = filters.priceRange;
      filtered = filtered.filter(product => {
        const price = product.price / 1000; // Конвертируем цену
        return price >= minPrice && price <= maxPrice;
      });
    }
    
    setFilteredProducts(filtered);
    
    // Обновляем пагинацию на основе отфильтрованных товаров
    const newTotalPages = Math.ceil(filtered.length / limit);
    setTotalPages(newTotalPages);
    setHasNextPage(filtered.length > limit);
    setDisplayedProducts(filtered.slice(0, limit));
    
    // Если текущая страница больше нового количества страниц, сбрасываем на первую
    if (page > newTotalPages && newTotalPages > 0) {
      setPage(1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="category-page-container">
      {/* Заголовок */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => window.history.back()}
          sx={{ 
            mb: { xs: 1.5, md: 2 }, 
            textTransform: 'none',
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}
        >
          Назад
        </Button>
        
        {categoryInfo && (
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: { xs: 1, md: 2 }, 
                fontWeight: 300,
                fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.125rem' }
              }}
            >
              {categoryInfo.name}
            </Typography>
            {categoryInfo.description && (
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                {categoryInfo.description}
              </Typography>
            )}
          </Box>
        )}

        {/* Подкатегории */}
        {subcategories.length > 0 && (
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 1, md: 2 }, 
                fontWeight: 500,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Подкатегории
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.5, md: 1 }, 
              flexWrap: 'wrap' 
            }}>
              {subcategories.map((subcategory) => (
                <Chip
                  key={subcategory.id}
                  label={subcategory.name}
                  onClick={() => handleSubcategoryClick(subcategory)}
                  variant="outlined"
                  sx={{
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    height: { xs: 28, md: 32 },
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Сортировка и фильтры - временно отключено */}
        {/* <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, md: 2 }, 
          mb: { xs: 2, md: 3 }, 
          alignItems: { xs: 'stretch', sm: 'center' } 
        }}>
          <FormControl sx={{ 
            minWidth: { xs: '100%', sm: 200 },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.875rem', md: '1rem' }
            }
          }}>
            <InputLabel>Сортировка</InputLabel>
            <Select
              value={sortBy}
              label="Сортировка"
              onChange={handleSortChange}
            >
              <MenuItem value="newest">Сначала новые</MenuItem>
              <MenuItem value="price_asc">Цена: по возрастанию</MenuItem>
              <MenuItem value="price_desc">Цена: по убыванию</MenuItem>
              <MenuItem value="name_asc">Название: А-Я</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton
            onClick={() => setShowFilters(!showFilters)}
            sx={{ 
              border: '1px solid #e0e0e0',
              backgroundColor: showFilters ? 'primary.main' : 'white',
              color: showFilters ? 'white' : 'primary.main',
              alignSelf: { xs: 'flex-start', sm: 'center' },
              '&:hover': {
                backgroundColor: showFilters ? 'primary.dark' : 'grey.100'
              }
            }}
          >
            <FilterList />
          </IconButton>
          
          {Object.keys(activeFilters).length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' }, 
              gap: { xs: 1, md: 1 } 
            }}>
              <Chip 
                label={`Активные фильтры: ${Object.keys(activeFilters).length}`}
                color="primary"
                variant="outlined"
                sx={{ 
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  height: { xs: 28, md: 32 }
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={clearAllFilters}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
              >
                Очистить все
              </Button>
            </Box>
          )}
        </Box> */}
      </Box>

      {/* Фильтры - временно отключено */}
      {/* <CategoryFilters
        categoryId={categoryId}
        onFiltersChange={handleFiltersChange}
        isOpen={showFilters}
      /> */}

      {/* Список товаров */}
      {(filteredProducts.length > 0 || products.length > 0) ? (
        <>
          <Box className="product-grid" sx={{ alignItems: 'stretch' }}>
            {(filteredProducts.length > 0 ? filteredProducts : displayedProducts)
              .map((product, index) => (
              <ProductCard
                key={`${product.id}-${index}`}
                product={product}
                onClick={() => handleProductClick(product.id)}
                showColors={true}
                showFavorite={true}
                variant="default"
              />
            ))}
          </Box>

          {/* Индикатор загрузки дополнительных результатов */}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 3, md: 4 } }}>
              <CircularProgress size={40} />
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 2, 
                  alignSelf: 'center',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Загружаем еще товары...
              </Typography>
            </Box>
          )}

          {/* Кнопка "Загрузить еще" для ручной загрузки */}
          {hasNextPage && !loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 } }}>
              <Button
                variant="outlined"
                onClick={() => setPage(prev => prev + 1)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Загрузить еще товары
              </Button>
            </Box>
          )}

          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mt: { xs: 3, md: 4 } 
            }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }
                }}
              />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Страница {page} из {totalPages}
              </Typography>
            </Box>
          )}
          
          {/* Информация о фильтрации */}
          {filteredProducts.length > 0 && (
            <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 2 } }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                Показано {filteredProducts.length} из {products.length} товаров
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 8 } }}>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
          >
            В данной категории нет товаров
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CategoryPage;
